import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { ARTICLES } from "@/data/articles";
import { CtaBand } from "@/pages/Home";
import Seo from "@/components/Seo";

export default function Resources() {
  const itemListJsonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Sign Questions Answered",
    "itemListElement": ARTICLES.map((a, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "name": a.title,
      "url": `https://rvasigns.com/resources/${a.slug}`,
    })),
  };

  return (
    <div data-testid="resources-page">
      <Seo
        path="/resources"
        title="Sign Questions Answered"
        description="Plainspoken answers to the questions Richmond business owners ask most often: monument signs, building signs, window graphics, permits, landlord approvals, and quote reviews."
        jsonLd={itemListJsonLd}
      />
      <section className="pt-20 pb-16 lg:pt-28 lg:pb-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <span className="w-10 h-0.5 bg-[#F97316]" />
            <span className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#F97316]">Sign Questions Answered</span>
          </div>
          <h1 className="font-heading text-[#1A1A1A] tracking-tight leading-[0.98] max-w-4xl" style={{ fontWeight: 900, fontSize: "clamp(2.5rem, 5.5vw, 4.5rem)" }} data-testid="resources-headline">
            Sign Questions Answered.
          </h1>
          <p className="mt-8 text-lg text-[#2B2F33]/80 leading-relaxed max-w-2xl">
            Most people only deal with signs a handful of times. These are some of the questions I hear most often from business owners, property managers, and contractors around Richmond.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 bg-white" data-testid="resources-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1A1A1A]/10 border border-[#1A1A1A]/10">
          {ARTICLES.map((a, i) => (
            <Link
              key={a.slug}
              to={`/resources/${a.slug}`}
              className="bg-white p-8 lg:p-10 group flex flex-col hover:bg-[#F8F8F5] transition-colors"
              data-testid={`article-card-${a.slug}`}
            >
              <div className="font-heading text-sm text-[#F97316] tracking-widest" style={{ fontWeight: 800 }}>{String(i + 1).padStart(2, "0")}</div>
              <div className="aspect-[16/10] mt-6 img-zoom overflow-hidden bg-[#F8F8F5]">
                <img
                  src={a.coverImage}
                  alt={a.title}
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
              <div className="text-[10px] tracking-[0.22em] uppercase text-[#F97316] font-bold mt-6">{a.category}</div>
              <h2 className="font-heading text-xl lg:text-2xl tracking-tight text-[#1A1A1A] mt-3 leading-snug" style={{ fontWeight: 700 }}>{a.title}</h2>
              <p className="text-sm text-[#2B2F33]/70 leading-relaxed mt-3 flex-1">{a.excerpt}</p>
              <div className="mt-5 text-xs tracking-tight text-[#2B2F33]/60 flex items-center justify-between">
                <span>{a.readTime}</span>
                <ArrowUpRight className="w-4 h-4 text-[#1A1A1A] group-hover:text-[#F97316] transition-colors" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      <CtaBand />
    </div>
  );
}
